import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Database, Search, Grid, List, Settings2, ArrowUp, ArrowDown, FileText, MoreHorizontal, Pencil, Trash2, Calendar, Type, LayoutGrid, Lock } from "lucide-react";
import { EntityLockButton } from "@/components/EntityLockButton";
import { PinVerificationDialog } from "@/components/PinVerificationDialog";
import { useViewPreferences } from "@/hooks/use-view-preferences";
import type { MastermindSpace } from "@shared/schema";
import { marked } from "marked";
import DOMPurify from "dompurify";

const stripHtml = (html: string | null | undefined): string => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

const isHtml = (str: string): boolean => {
  return /<[a-z][\s\S]*>/i.test(str);
};

const ensureHtmlContent = (content: string | null | undefined): string => {
  if (!content) return "";
  if (isHtml(content)) return content;
  try {
    const html = marked.parse(content, { async: false }) as string;
    return DOMPurify.sanitize(html);
  } catch (error) {
    const escaped = content
      .split('\n\n')
      .map(p => `<p>${p.replace(/\n/g, '<br/>')}</p>`)
      .join('');
    return escaped;
  }
};

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuCheckboxItem } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RichTextEditor } from "@/components/RichTextEditor";
import { formatDistanceToNow } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { queryClient } from "@/lib/queryClient";
import { knowledgeBaseService } from "@/services/knowledgeBaseService";
import { mastermindSpacesService } from "@/services/mastermindSpacesService";
import EmptyState from "@/components/EmptyState";
import emptyImage from '@assets/generated_images/Empty_knowledge_state_illustration_2e5a1887.png';

type DocSortField = "title" | "type" | "updatedAt" | "createdAt";

interface ColumnConfig {
  id: string;
  label: string;
  visible: boolean;
}

interface KnowledgeBaseDocument {
  id: string;
  userId: string;
  spaceId: string | null;
  title: string;
  content: string | null;
  type: string | null;
  folderId: string | null;
  googleDocsId: string | null;
  videoUrl: string | null;
  metadata: any;
  createdAt: string;
  updatedAt: string;
  isLocked?: string | null;
  lockedAt?: string | null;
}

export default function KnowledgeBaseDocuments() {
  const { toast } = useToast();
  const { currentWorkspaceId } = useWorkspace();
  
  const {
    viewMode,
    setViewMode,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    setSort,
  } = useViewPreferences<DocSortField>({
    storageKey: "kbDocs",
    defaultViewMode: "grid",
    defaultSortField: "updatedAt",
    defaultSortDirection: "desc",
  });

  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState<string>("all");
  const [selectedItem, setSelectedItem] = useState<KnowledgeBaseDocument | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<KnowledgeBaseDocument | null>(null);
  const [itemToRename, setItemToRename] = useState<KnowledgeBaseDocument | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [editForm, setEditForm] = useState({
    title: "",
    content: "",
    type: "",
  });
  
  const [columns, setColumns] = useState<ColumnConfig[]>([
    { id: "title", label: "Title", visible: true },
    { id: "type", label: "Type", visible: true },
    { id: "space", label: "Space", visible: true },
    { id: "date", label: "Last Updated", visible: true },
  ]);

  // Track unlocked documents for this session and PIN dialog state
  const [unlockedDocIds, setUnlockedDocIds] = useState<Set<string>>(new Set());
  const [showPinDialogForDoc, setShowPinDialogForDoc] = useState<KnowledgeBaseDocument | null>(null);

  const { data: documents = [], isLoading } = useQuery<KnowledgeBaseDocument[]>({
    queryKey: ["/api/knowledgebase", currentWorkspaceId ?? null],
    queryFn: async () => {
      return await knowledgeBaseService.getKnowledgeBaseItems(undefined, currentWorkspaceId ?? null);
    },
  });

  const { data: spaces = [] } = useQuery<MastermindSpace[]>({
    queryKey: ["/api/spaces", currentWorkspaceId ?? null],
    queryFn: async () => {
      return await mastermindSpacesService.getMastermindSpaces(currentWorkspaceId ?? null);
    },
  });

  const getSpaceName = (spaceId: string | null) => {
    if (!spaceId) return null;
    const space = spaces.find(s => s.id === spaceId);
    return space?.name || null;
  };

  const handleColumnToggle = (columnId: string) => {
    setColumns(columns.map(col => 
      col.id === columnId ? { ...col, visible: !col.visible } : col
    ));
  };

  const moveColumn = (fromIndex: number, toIndex: number) => {
    const newColumns = [...columns];
    const [removed] = newColumns.splice(fromIndex, 1);
    newColumns.splice(toIndex, 0, removed);
    setColumns(newColumns);
  };

  const handleSelectAll = () => {
    if (selectedItems.length === filteredItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredItems.map(item => item.id));
    }
  };

  const handleSelectItem = (itemId: string) => {
    if (selectedItems.includes(itemId)) {
      setSelectedItems(selectedItems.filter(id => id !== itemId));
    } else {
      setSelectedItems([...selectedItems, itemId]);
    }
  };

  const filteredItems = documents
    .filter(item => {
      const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.content?.toLowerCase() || "").includes(searchQuery.toLowerCase());
      const matchesType = selectedType === "all" || item.type === selectedType;
      return matchesSearch && matchesType;
    })
    .sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case "title":
          comparison = a.title.localeCompare(b.title);
          break;
        case "type":
          comparison = (a.type || "").localeCompare(b.type || "");
          break;
        case "updatedAt":
          comparison = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          break;
        case "createdAt":
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });

  const updateItemMutation = useMutation({
    mutationFn: async (data: { id: string; updates: Partial<KnowledgeBaseDocument> }) => {
      return await knowledgeBaseService.updateKnowledgeBaseItem(data.id, data.updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/knowledgebase"] });
      toast({
        title: "Document Updated",
        description: "Document has been updated successfully",
      });
      setIsEditModalOpen(false);
    },
    onError: () => {
      toast({
        title: "Update Failed",
        description: "Failed to update document",
        variant: "destructive",
      });
    },
  });

  const deleteItemMutation = useMutation({
    mutationFn: async (id: string) => {
      return await knowledgeBaseService.deleteKnowledgeBaseItem(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/knowledgebase"] });
      toast({
        title: "Document Deleted",
        description: "Document has been deleted successfully",
      });
      setIsDeleteDialogOpen(false);
      setItemToDelete(null);
    },
    onError: () => {
      toast({
        title: "Delete Failed",
        description: "Failed to delete document",
        variant: "destructive",
      });
    },
  });

  const handleDeleteClick = (e: React.MouseEvent, doc: KnowledgeBaseDocument) => {
    e.stopPropagation();
    setItemToDelete(doc);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (itemToDelete) {
      deleteItemMutation.mutate(itemToDelete.id);
    }
  };

  const handleOpenItem = (item: KnowledgeBaseDocument) => {
    // If document is locked and not unlocked in this session, show PIN dialog
    if (item.isLocked === "true" && !unlockedDocIds.has(item.id)) {
      setShowPinDialogForDoc(item);
      return;
    }
    setSelectedItem(item);
    setEditForm({
      title: item.title || "",
      content: ensureHtmlContent(item.content),
      type: item.type || "note",
    });
    setIsEditModalOpen(true);
  };

  const handleDocPinSuccess = () => {
    if (showPinDialogForDoc) {
      // Mark this document as unlocked for this session
      setUnlockedDocIds(prev => {
        const next = new Set(prev);
        next.add(showPinDialogForDoc.id);
        return next;
      });
      // Now open the document
      setSelectedItem(showPinDialogForDoc);
      setEditForm({
        title: showPinDialogForDoc.title || "",
        content: ensureHtmlContent(showPinDialogForDoc.content),
        type: showPinDialogForDoc.type || "note",
      });
      setIsEditModalOpen(true);
      setShowPinDialogForDoc(null);
    }
  };

  // Auto-relock documents on component unmount
  useEffect(() => {
    return () => {
      unlockedDocIds.forEach(docId => {
        knowledgeBaseService.lockDocument(docId, true).catch(err => {
          console.error("Failed to auto-relock document:", err);
        });
      });
    };
  }, [unlockedDocIds]);

  const handleSaveItem = () => {
    if (!selectedItem) return;
    updateItemMutation.mutate({
      id: selectedItem.id,
      updates: editForm,
    });
  };

  const handleRenameClick = (e: React.MouseEvent, doc: KnowledgeBaseDocument) => {
    e.stopPropagation();
    setItemToRename(doc);
    setRenameValue(doc.title);
    setIsRenameDialogOpen(true);
  };

  const confirmRename = () => {
    if (itemToRename && renameValue.trim()) {
      updateItemMutation.mutate({
        id: itemToRename.id,
        updates: { title: renameValue.trim() },
      });
      setIsRenameDialogOpen(false);
      setItemToRename(null);
      setRenameValue("");
    }
  };

  const sortOptions = [
    { value: "updatedAt", label: "Last Updated" },
    { value: "createdAt", label: "Date Created" },
    { value: "title", label: "Title" },
    { value: "type", label: "Type" },
  ] as const;

  const hasItems = documents.length > 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Loading documents...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-2">Knowledge Base Documents</h1>
        <p className="text-muted-foreground">Document storage from AI generation clips and personal content</p>
      </div>

      {hasItems ? (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search documents..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="input-knowledge-search"
              />
            </div>
            
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger className="w-[140px]" data-testid="select-type-filter">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="note">Note</SelectItem>
                <SelectItem value="research">Research</SelectItem>
                <SelectItem value="plan">Plan</SelectItem>
                <SelectItem value="idea">Idea</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortField} onValueChange={(v) => setSortField(v as DocSortField)}>
              <SelectTrigger className="w-[150px]" data-testid="select-sort-field">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                {sortOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              size="icon"
              onClick={() => setSortDirection(sortDirection === "asc" ? "desc" : "asc")}
              data-testid="button-sort-direction"
            >
              {sortDirection === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
            </Button>

            <div className="flex items-center border rounded-md">
              <Button
                variant={viewMode === "grid" ? "default" : "ghost"}
                size="icon"
                onClick={() => setViewMode("grid")}
                className="rounded-r-none"
                data-testid="button-view-grid"
              >
                <Grid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "list" ? "default" : "ghost"}
                size="icon"
                onClick={() => setViewMode("list")}
                className="rounded-l-none"
                data-testid="button-view-list"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>

            {viewMode === "list" && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" data-testid="button-column-settings">
                    <Settings2 className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuLabel>Toggle Columns</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {columns.map((column) => (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      checked={column.visible}
                      onCheckedChange={() => handleColumnToggle(column.id)}
                      data-testid={`checkbox-column-${column.id}`}
                    >
                      {column.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {viewMode === "grid" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredItems.map((doc) => (
                <Card 
                  key={doc.id} 
                  className="p-4 hover-elevate cursor-pointer group/card"
                  onClick={() => handleOpenItem(doc)}
                  data-testid={`doc-card-${doc.id}`}
                >
                  <div className="space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        {doc.isLocked === "true" && (
                          <Lock className="h-4 w-4 text-amber-500 shrink-0" />
                        )}
                        <h3 className="font-semibold line-clamp-2">{doc.title}</h3>
                        {doc.isLocked === "true" && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 shrink-0 text-amber-500 border-amber-500/30">
                            Locked
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover/card:opacity-100" data-testid={`button-actions-grid-${doc.id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleRenameClick(e, doc); }}>
                              <Type className="h-4 w-4 mr-2" />
                              Rename
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenItem(doc); }}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <div className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                              <EntityLockButton
                                entityType="document"
                                entityId={doc.id}
                                isLocked={doc.isLocked === "true"}
                                size="menu"
                                customTitle={doc.isLocked === "true" ? "Unlock document" : "Lock document"}
                              />
                            </div>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); handleDeleteClick(e, doc); }}>
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <Badge variant="outline" className="capitalize">
                          {doc.type || "—"}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-3">{stripHtml(doc.content) || "No content"}</p>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        <span>{doc.updatedAt && !isNaN(new Date(doc.updatedAt).getTime()) 
                          ? formatDistanceToNow(new Date(doc.updatedAt), { addSuffix: true })
                          : "Unknown"}</span>
                      </div>
                      {doc.spaceId && getSpaceName(doc.spaceId) && (
                        <Badge variant="secondary" className="text-xs">
                          <LayoutGrid className="h-3 w-3 mr-1" />
                          {getSpaceName(doc.spaceId)}
                        </Badge>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <ScrollArea className="h-[600px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedItems.length === filteredItems.length && filteredItems.length > 0}
                          onCheckedChange={handleSelectAll}
                          data-testid="checkbox-select-all"
                        />
                      </TableHead>
                      {columns.filter(c => c.visible).map(column => (
                        <TableHead key={column.id}>{column.label}</TableHead>
                      ))}
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="h-32 text-center">
                          <div className="flex flex-col items-center justify-center text-muted-foreground">
                            <FileText className="h-8 w-8 mb-2" />
                            <p className="text-sm">No documents found</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredItems.map((doc) => (
                        <TableRow 
                          key={doc.id} 
                          className="hover-elevate cursor-pointer"
                          onClick={() => handleOpenItem(doc)}
                          data-testid={`doc-row-${doc.id}`}
                        >
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedItems.includes(doc.id)}
                              onCheckedChange={() => handleSelectItem(doc.id)}
                              data-testid={`checkbox-doc-${doc.id}`}
                            />
                          </TableCell>
                          {columns.filter(c => c.visible).map(column => {
                            switch (column.id) {
                              case "title":
                                return (
                                  <TableCell key="title">
                                    <div className="max-w-md">
                                      <div className="flex items-center gap-2">
                                        {doc.isLocked === "true" && (
                                          <Lock className="h-4 w-4 text-amber-500 shrink-0" />
                                        )}
                                        <div className="font-medium truncate">{doc.title}</div>
                                        {doc.isLocked === "true" && (
                                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 shrink-0 text-amber-500 border-amber-500/30">
                                            Locked
                                          </Badge>
                                        )}
                                      </div>
                                      <div className="text-xs text-muted-foreground truncate">{stripHtml(doc.content) || "No content"}</div>
                                    </div>
                                  </TableCell>
                                );
                              case "type":
                                return (
                                  <TableCell key="type">
                                    <Badge variant="outline" className="capitalize">
                                      {doc.type || "—"}
                                    </Badge>
                                  </TableCell>
                                );
                              case "space":
                                const spaceName = getSpaceName(doc.spaceId);
                                return (
                                  <TableCell key="space">
                                    {spaceName ? (
                                      <Badge variant="secondary" className="text-xs">
                                        <LayoutGrid className="h-3 w-3 mr-1" />
                                        {spaceName}
                                      </Badge>
                                    ) : (
                                      <span className="text-sm text-muted-foreground">—</span>
                                    )}
                                  </TableCell>
                                );
                              case "date":
                                return (
                                  <TableCell key="date">
                                    <span className="text-sm text-muted-foreground">
                                      {doc.updatedAt && !isNaN(new Date(doc.updatedAt).getTime())
                                        ? formatDistanceToNow(new Date(doc.updatedAt), { addSuffix: true })
                                        : "Unknown"}
                                    </span>
                                  </TableCell>
                                );
                              default:
                                return null;
                            }
                          })}
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" data-testid={`button-actions-${doc.id}`}>
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem 
                                  onClick={(e) => handleRenameClick(e, doc)}
                                  data-testid={`menu-rename-${doc.id}`}
                                >
                                  <Type className="h-4 w-4 mr-2" />
                                  Rename
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenItem(doc);
                                  }}
                                  data-testid={`menu-edit-${doc.id}`}
                                >
                                  <Pencil className="h-4 w-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <div className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                                  <EntityLockButton
                                    entityType="document"
                                    entityId={doc.id}
                                    isLocked={doc.isLocked === "true"}
                                  />
                                  <span className="ml-2 text-sm">{doc.isLocked === "true" ? "Unlock" : "Lock"}</span>
                                </div>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  className="text-destructive"
                                  onClick={(e) => handleDeleteClick(e, doc)}
                                  data-testid={`menu-delete-${doc.id}`}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </Card>
          )}
        </>
      ) : (
        <EmptyState
          icon={Database}
          title="No documents yet"
          description="Start creating documents from AI chat clips or add your own personal content."
          actionLabel="Create Your First Document"
          onAction={() => console.log('Create first document')}
          imageSrc={emptyImage}
        />
      )}

      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Document</DialogTitle>
            <DialogDescription>
              Update your document details
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-title">Title</Label>
              <Input
                id="edit-title"
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                data-testid="input-edit-title"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-content">Content</Label>
              <RichTextEditor
                content={editForm.content}
                onChange={(content) => setEditForm({ ...editForm, content })}
                placeholder="Start writing your content..."
                data-testid="editor-content"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-type">Type</Label>
              <Select
                value={editForm.type}
                onValueChange={(value) => setEditForm({ ...editForm, type: value })}
              >
                <SelectTrigger id="edit-type" data-testid="select-edit-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="note">Note</SelectItem>
                  <SelectItem value="research">Research</SelectItem>
                  <SelectItem value="plan">Plan</SelectItem>
                  <SelectItem value="idea">Idea</SelectItem>
                  <SelectItem value="meeting">Meeting Notes</SelectItem>
                  <SelectItem value="journal">Journal</SelectItem>
                  <SelectItem value="code">Code Snippet</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsEditModalOpen(false)}
              data-testid="button-cancel-edit"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveItem}
              disabled={updateItemMutation.isPending}
              data-testid="button-save-edit"
            >
              {updateItemMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{itemToDelete?.title}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setItemToDelete(null);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteItemMutation.isPending}
            >
              {deleteItemMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={isRenameDialogOpen} onOpenChange={setIsRenameDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rename Document</DialogTitle>
            <DialogDescription>
              Enter a new name for this document.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Input
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              placeholder="Document name"
              data-testid="input-rename"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  confirmRename();
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsRenameDialogOpen(false);
                setItemToRename(null);
                setRenameValue("");
              }}
              data-testid="button-cancel-rename"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmRename}
              disabled={!renameValue.trim() || updateItemMutation.isPending}
              data-testid="button-confirm-rename"
            >
              {updateItemMutation.isPending ? "Renaming..." : "Rename"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PinVerificationDialog
        open={showPinDialogForDoc !== null}
        onOpenChange={(open) => !open && setShowPinDialogForDoc(null)}
        onSuccess={handleDocPinSuccess}
        title="Unlock Document"
        description="This document is locked. Enter your PIN to view its contents."
      />
    </div>
  );
}
